import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RegistrationState, SongData, Writer, WriterSplit, RegistrationResult } from '../types';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useAuth } from './AuthContext';

// Generate unique ID
const generateId = () => Math.random().toString(36).substring(2, 9);

// Initial state
const initialState: RegistrationState = {
  currentStep: 1,
  songData: {
    title: '',
    artist: '',
    additionalArtists: [],
    releaseDate: '',
    duration: '',
    isrc: '',
  },
  writers: [],
  splits: [],
  isSubmitting: false,
  registrationComplete: false,
  registrationResult: null,
};

// Extended state with registration ID
interface ExtendedState extends RegistrationState {
  registrationId: string | null;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved: Date | null;
}

const initialExtendedState: ExtendedState = {
  ...initialState,
  registrationId: null,
  saveStatus: 'idle',
  lastSaved: null,
};

// Action types
type Action =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SET_SONG_DATA'; payload: Partial<SongData> }
  | { type: 'ADD_ARTIST' }
  | { type: 'REMOVE_ARTIST'; payload: number }
  | { type: 'UPDATE_ARTIST'; payload: { index: number; value: string } }
  | { type: 'SET_WRITERS'; payload: Writer[] }
  | { type: 'ADD_WRITER' }
  | { type: 'REMOVE_WRITER'; payload: string }
  | { type: 'UPDATE_WRITER'; payload: { id: string; updates: Partial<Writer> } }
  | { type: 'CONFIRM_WRITER'; payload: string }
  | { type: 'SET_SPLITS'; payload: WriterSplit[] }
  | { type: 'UPDATE_SPLIT'; payload: { writerId: string; field: 'author' | 'composer'; value: number } }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'SET_REGISTRATION_COMPLETE'; payload: RegistrationResult }
  | { type: 'RESET' }
  | { type: 'SET_REGISTRATION_ID'; payload: string | null }
  | { type: 'SET_SAVE_STATUS'; payload: 'idle' | 'saving' | 'saved' | 'error' }
  | { type: 'SET_LAST_SAVED'; payload: Date | null }
  | { type: 'LOAD_REGISTRATION'; payload: Partial<ExtendedState> };

// Reducer
function registrationReducer(state: ExtendedState, action: Action): ExtendedState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };

    case 'SET_SONG_DATA':
      return {
        ...state,
        songData: { ...state.songData, ...action.payload },
      };

    case 'ADD_ARTIST':
      return {
        ...state,
        songData: {
          ...state.songData,
          additionalArtists: [...state.songData.additionalArtists, ''],
        },
      };

    case 'REMOVE_ARTIST':
      return {
        ...state,
        songData: {
          ...state.songData,
          additionalArtists: state.songData.additionalArtists.filter((_, i) => i !== action.payload),
        },
      };

    case 'UPDATE_ARTIST':
      return {
        ...state,
        songData: {
          ...state.songData,
          additionalArtists: state.songData.additionalArtists.map((a, i) =>
            i === action.payload.index ? action.payload.value : a
          ),
        },
      };

    case 'SET_WRITERS':
      return {
        ...state,
        writers: action.payload,
        // Initialize splits when writers are set
        splits: action.payload.map(w => ({
          writerId: w.id,
          writerName: w.name,
          author: 0,
          composer: 0,
        })),
      };

    case 'ADD_WRITER':
      const newWriter: Writer = {
        id: generateId(),
        name: '',
        role: 'Writer',
        ipi: '',
        ipiMatches: [],
        confirmed: false,
      };
      return {
        ...state,
        writers: [...state.writers, newWriter],
        splits: [
          ...state.splits,
          { writerId: newWriter.id, writerName: '', author: 0, composer: 0 },
        ],
      };

    case 'REMOVE_WRITER':
      return {
        ...state,
        writers: state.writers.filter(w => w.id !== action.payload),
        splits: state.splits.filter(s => s.writerId !== action.payload),
      };

    case 'UPDATE_WRITER':
      return {
        ...state,
        writers: state.writers.map(w =>
          w.id === action.payload.id ? { ...w, ...action.payload.updates } : w
        ),
        splits: state.splits.map(s =>
          s.writerId === action.payload.id
            ? { ...s, writerName: action.payload.updates.name || s.writerName }
            : s
        ),
      };

    case 'CONFIRM_WRITER':
      return {
        ...state,
        writers: state.writers.map(w =>
          w.id === action.payload ? { ...w, confirmed: !w.confirmed } : w
        ),
      };

    case 'SET_SPLITS':
      return { ...state, splits: action.payload };

    case 'UPDATE_SPLIT':
      return {
        ...state,
        splits: state.splits.map(s =>
          s.writerId === action.payload.writerId
            ? { ...s, [action.payload.field]: action.payload.value }
            : s
        ),
      };

    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };

    case 'SET_REGISTRATION_COMPLETE':
      return {
        ...state,
        isSubmitting: false,
        registrationComplete: true,
        registrationResult: action.payload,
      };

    case 'RESET':
      return initialExtendedState;

    case 'SET_REGISTRATION_ID':
      return { ...state, registrationId: action.payload };

    case 'SET_SAVE_STATUS':
      return { ...state, saveStatus: action.payload };

    case 'SET_LAST_SAVED':
      return { ...state, lastSaved: action.payload };

    case 'LOAD_REGISTRATION':
      return { ...state, ...action.payload };

    default:
      return state;
  }
}

// Context
interface RegistrationContextType {
  state: ExtendedState;
  dispatch: React.Dispatch<Action>;
  // Convenience methods
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  reset: () => void;
  // Persistence methods
  saveToSupabase: () => Promise<void>;
  markAsCompleted: (airtableRecordIds: string[]) => Promise<void>;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

// Provider
export function RegistrationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(registrationReducer, initialExtendedState);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLoadingRef = useRef(false);
  const lastSavedStateRef = useRef<string>('');
  const stateRef = useRef(state);
  stateRef.current = state;

  // Load registration from URL param on mount
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (resumeId && isSupabaseConfigured() && user && !isLoadingRef.current) {
      isLoadingRef.current = true;
      loadRegistration(resumeId);
    }
  }, [searchParams, user]);

  // Load registration from Supabase
  const loadRegistration = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('registrations')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        dispatch({
          type: 'LOAD_REGISTRATION',
          payload: {
            registrationId: data.id,
            currentStep: data.current_step || 1,
            songData: {
              title: data.title || '',
              artist: data.artist || '',
              additionalArtists: data.additional_artists || [],
              releaseDate: data.release_date || '',
              duration: data.duration || '',
              isrc: data.isrc || '',
            },
            writers: (data.writers as Writer[]) || [],
            splits: (data.splits as WriterSplit[]) || [],
            lastSaved: new Date(data.updated_at),
          },
        });

        // Clear the resume param from URL
        setSearchParams({});
      }
    } catch (err) {
      console.error('Failed to load registration:', err);
    } finally {
      isLoadingRef.current = false;
    }
  };

  // Save to Supabase (reads state from ref for a stable callback identity)
  const saveToSupabase = useCallback(async () => {
    const s = stateRef.current;
    if (!isSupabaseConfigured() || !user) return;

    // Don't save if registration is complete
    if (s.registrationComplete) return;

    // Create state snapshot for comparison
    const stateSnapshot = JSON.stringify({
      songData: s.songData,
      writers: s.writers,
      splits: s.splits,
      currentStep: s.currentStep,
    });

    // Skip if nothing changed
    if (stateSnapshot === lastSavedStateRef.current) return;

    dispatch({ type: 'SET_SAVE_STATUS', payload: 'saving' });

    try {
      const registrationData = {
        user_id: user.id,
        status: 'draft' as const,
        title: s.songData.title || null,
        artist: s.songData.artist || null,
        additional_artists: s.songData.additionalArtists,
        release_date: s.songData.releaseDate || null,
        duration: s.songData.duration || null,
        isrc: s.songData.isrc || null,
        writers: s.writers,
        splits: s.splits,
        current_step: s.currentStep,
      };

      let result;

      if (s.registrationId) {
        // Update existing
        result = await supabase
          .from('registrations')
          .update(registrationData)
          .eq('id', s.registrationId)
          .select()
          .single();
      } else {
        // Create new
        result = await supabase
          .from('registrations')
          .insert(registrationData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      if (result.data && !s.registrationId) {
        dispatch({ type: 'SET_REGISTRATION_ID', payload: result.data.id });
      }

      lastSavedStateRef.current = stateSnapshot;
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'saved' });
      dispatch({ type: 'SET_LAST_SAVED', payload: new Date() });
    } catch (err) {
      console.error('Failed to save registration:', err);
      dispatch({ type: 'SET_SAVE_STATUS', payload: 'error' });
    }
  }, [user]);

  // Mark registration as completed
  const markAsCompleted = useCallback(async (airtableRecordIds: string[]) => {
    if (!isSupabaseConfigured() || !user || !state.registrationId) return;

    try {
      await supabase
        .from('registrations')
        .update({
          status: 'completed',
          airtable_record_ids: airtableRecordIds,
        })
        .eq('id', state.registrationId);
    } catch (err) {
      console.error('Failed to mark registration as completed:', err);
    }
  }, [state.registrationId, user]);

  // Auto-save on step change
  useEffect(() => {
    if (state.currentStep > 1 && isSupabaseConfigured() && user) {
      saveToSupabase();
    }
  }, [state.currentStep]);

  // Debounced auto-save on data changes
  useEffect(() => {
    if (!isSupabaseConfigured() || !user) return;
    if (state.registrationComplete) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for auto-save (3 seconds after last change)
    saveTimeoutRef.current = setTimeout(() => {
      saveToSupabase();
    }, 3000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state.songData, state.writers, state.splits]);

  const goToStep = useCallback((step: number) => dispatch({ type: 'SET_STEP', payload: step }), []);
  const nextStep = useCallback(() => {
    dispatch({ type: 'SET_STEP', payload: stateRef.current.currentStep + 1 });
  }, []);
  const prevStep = useCallback(() => {
    dispatch({ type: 'SET_STEP', payload: Math.max(1, stateRef.current.currentStep - 1) });
  }, []);
  const reset = useCallback(() => {
    lastSavedStateRef.current = '';  // Clear saved state comparison ref to prevent stale data
    isLoadingRef.current = false;    // Reset loading flag
    dispatch({ type: 'RESET' });
  }, []);

  const contextValue = useMemo(() => ({
    state,
    dispatch,
    goToStep,
    nextStep,
    prevStep,
    reset,
    saveToSupabase,
    markAsCompleted,
  }), [state, goToStep, nextStep, prevStep, reset, saveToSupabase, markAsCompleted]);

  return (
    <RegistrationContext.Provider value={contextValue}>
      {children}
    </RegistrationContext.Provider>
  );
}

// Hook
export function useRegistration() {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
}
